import {
  queryOptions,
  useQuery,
  type UseQueryResult,
} from "@tanstack/react-query";
import { useCallback } from "react";

import type { ApiClient } from "@/api/client";
import { useApiClient } from "@/api/client-context";
import { type PermissionRequests, qk } from "@/api/query-keys";

import { type HttpMethod, PERMISSION_TARGETS } from "./permission-targets";

/** POST /v1.0/permissions-check のレスポンス。 */
export type PermissionResults = Readonly<
  Record<string, Readonly<Record<string, boolean>>>
>;

/**
 * アプリ全体で使う静的ターゲットのバッチ。シェルで 1 回だけ取得する。
 * モジュール定数にしておくことで queryKey が安定する。
 */
export const SHELL_PERMISSION_REQUESTS: PermissionRequests = {
  [PERMISSION_TARGETS.processInstanceListForMe]: ["POST"],
  [PERMISSION_TARGETS.processInstanceList]: ["POST"],
  [PERMISSION_TARGETS.processModelList]: ["GET", "POST"],
  [PERMISSION_TARGETS.processInstanceReportList]: ["GET"],
  [PERMISSION_TARGETS.taskList]: ["GET"],
  [PERMISSION_TARGETS.secretList]: ["GET"],
  [PERMISSION_TARGETS.userSearch]: ["GET"],
};

/**
 * ApiClient はセッション単位のインフラであってキャッシュキーではないため
 * queryKey に含めない（キーはプレーン JSON 値のみという規約も守れなくなる）。
 * options を関数に切り出すことで client を引数として扱う。
 */
export function permissionsQueryOptions(
  client: ApiClient,
  requests: PermissionRequests,
) {
  const fetchPermissions = async () => {
    const response = await client.post<{ results: PermissionResults }>(
      "/permissions-check",
      { requests_to_check: requests },
    );
    return response.results;
  };

  return queryOptions({
    queryKey: qk.auth.permissions(requests),
    queryFn: fetchPermissions,
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
  });
}

export function usePermissions(
  requests: PermissionRequests = SHELL_PERMISSION_REQUESTS,
): UseQueryResult<PermissionResults> {
  return useQuery(permissionsQueryOptions(useApiClient(), requests));
}

/**
 * 単一の可否。取得前・未定義の組み合わせは false（楽観的に見せない）。
 *
 * requests は usePermissions と同じものを渡すこと（同じ queryKey に相乗りし、
 * リクエストが増えない）。
 */
export function useCan(
  target: string,
  method: HttpMethod,
  requests: PermissionRequests = SHELL_PERMISSION_REQUESTS,
): boolean {
  const { data } = usePermissions(requests);
  return data?.[target]?.[method] ?? false;
}

/**
 * 可否を引く関数そのものを返す。ナビ項目のように「複数の組み合わせを
 * ループで判定する」場面ではフックを繰り返し呼べないためこちらを使う。
 */
export function useCanFn(
  requests: PermissionRequests = SHELL_PERMISSION_REQUESTS,
): (_target: string, _method: HttpMethod) => boolean {
  const { data } = usePermissions(requests);

  return useCallback(
    (target: string, method: HttpMethod) => data?.[target]?.[method] ?? false,
    [data],
  );
}
